/*
 * Профили AI-саммари. Раньше были захардкожены тут (3-я копия контракта).
 * Теперь источник правды — каталог в БД recorder-backend, фронт тянет список
 * через GET /api/v1/summary-profiles. Статика ниже — ФОЛБЭК на случай, пока
 * список не загрузился или бэк недоступен; так пикеры никогда не пустые.
 */
import { useEffect, useState } from "react";

import { fetchSummaryProfiles, type SummaryProfileMeta } from "./api/prompts";

export type { SummaryProfileMeta } from "./api/prompts";

/** Код профиля. Раньше был union ("sales"|...); теперь профили динамические →
 *  строка. Оставлен как алиас, чтобы существующие сигнатуры не переписывать. */
export type SummaryProfile = string;

/** Дефолтный профиль (исторический). Бэк может прислать свой default. */
export const DEFAULT_SUMMARY_PROFILE = "sales";

/** Статический фолбэк — текущие 4 профиля. Совпадает с сидом recorder-backend. */
export const FALLBACK_PROFILES: SummaryProfileMeta[] = [
  {
    slug: "sales",
    label: "Продажи",
    hint: "Клиентский / деловой звонок: решения, action items, договорённости, числа.",
  },
  {
    slug: "tech",
    label: "Технический созвон",
    hint: "Технический созвон: разбор вопросов и объяснения, решения по архитектуре, задачи, блокеры, trade-offs.",
  },
  {
    slug: "meetup",
    label: "Митап / выступление",
    hint: "Доклад / митап / лекция: ключевые тезисы, выводы, вопросы из зала.",
  },
  {
    slug: "interview",
    label: "Интервью",
    hint: "Интервью / собеседование: темы и ответы, сильные стороны, итог.",
  },
];

// Модульный кэш лейблов: чтобы summaryProfileLabel() оставался синхронным и его
// call-sites (табы/диалоги) не переписывать. Стартует со статики, дополняется
// загруженными с бэка лейблами.
const labelCache = new Map<string, string>(
  FALLBACK_PROFILES.map((p) => [p.slug, p.label]),
);

/** Человеческий лейбл по коду профиля; неизвестный код → сам код (forward-compat). */
export function summaryProfileLabel(profile: string): string {
  return labelCache.get(profile) ?? profile;
}

let cachedList: SummaryProfileMeta[] | null = null;
let cachedDefault = DEFAULT_SUMMARY_PROFILE;
let inflight: Promise<void> | null = null;

/** Однократная (кэшируемая) загрузка списка профилей. Никогда не бросает:
 *  при ошибке/пустом ответе оседает на FALLBACK_PROFILES. Паттерн как getAppConfig. */
function ensureLoaded(): Promise<void> {
  if (cachedList) return Promise.resolve();
  if (!inflight) {
    inflight = (async () => {
      try {
        const res = await fetchSummaryProfiles();
        if (res.profiles && res.profiles.length > 0) {
          cachedList = res.profiles;
          cachedDefault = res.default || DEFAULT_SUMMARY_PROFILE;
          for (const p of res.profiles) labelCache.set(p.slug, p.label);
        } else {
          cachedList = FALLBACK_PROFILES;
        }
      } catch {
        cachedList = FALLBACK_PROFILES;
      } finally {
        inflight = null;
      }
    })();
  }
  return inflight;
}

/** Сбросить кэш списка профилей. Следующий монтаж useSummaryProfiles() (или
 *  первый вызов ensureLoaded) перечитает каталог с бэка. Зовётся после
 *  admin-правок (create/update/archive/common), иначе пикеры и лейблы держат
 *  устаревшие названия до полной перезагрузки страницы. */
export function invalidateSummaryProfiles(): void {
  cachedList = null;
  inflight = null;
}

export interface UseSummaryProfiles {
  profiles: SummaryProfileMeta[];
  defaultProfile: string;
  loading: boolean;
}

/** Хук: список активных профилей для пикеров. До загрузки отдаёт фолбэк, после
 *  — реальный список с бэка (с авто-ре-рендером). Кэш общий на всё приложение. */
export function useSummaryProfiles(): UseSummaryProfiles {
  const [profiles, setProfiles] = useState<SummaryProfileMeta[]>(
    cachedList ?? FALLBACK_PROFILES,
  );
  const [defaultProfile, setDefaultProfile] = useState<string>(cachedDefault);
  const [loading, setLoading] = useState<boolean>(cachedList === null);

  useEffect(() => {
    if (cachedList) {
      setProfiles(cachedList);
      setDefaultProfile(cachedDefault);
      setLoading(false);
      return;
    }
    let alive = true;
    void ensureLoaded().then(() => {
      if (!alive) return;
      setProfiles(cachedList ?? FALLBACK_PROFILES);
      setDefaultProfile(cachedDefault);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { profiles, defaultProfile, loading };
}
